import React, { useCallback, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { notBlank } from "../util/ext";
import Paper from "@mui/material/Paper";
import { lensPath, mergeRight, omit, set } from "ramda";
import Grid from "@mui/material/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useUserUpdate } from "./userServices";
import TextField from "@mui/material/TextField";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  errorContainer: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.error.main,
  },
}));

const initialState = {
  password: "",
  confirm: "",
};

const ChangePasswordDialog = props => {
  const { open, onCancel, onOk, user } = props;
  const classes = useStyles();

  const [changePassword, setChangePassword] = useState(initialState);
  const [errors, setErrors] = useState([]);

  const updateUser = useUserUpdate();
  const handleApply = useCallback(async () => {
    if (
      notBlank(changePassword.password) &&
      notBlank(changePassword.confirm) &&
      changePassword.password === changePassword.confirm
    ) {
      const update = mergeRight(
        { password: changePassword.password },
        omit(["organizations", "profile"], user),
      );

      try {
        await updateUser.mutateAsync({
          resourceIds: { id: user.userId },
          data: update,
        });
        setChangePassword(initialState);
        setErrors([]);
        onOk();
      } catch (err) {
        setErrors([err.message]);
      }
    } else {
      setErrors(["Password Confirmation does not match."]);
    }
  }, [changePassword, user, updateUser, onOk]);

  const handleClose = useCallback(() => {
    setChangePassword(initialState);
    setErrors([]);
    onCancel();
  }, [onCancel]);

  const handleTextChange = path => event => {
    const value = event.target.value;
    setChangePassword(prev => set(lensPath(path), value, prev));
  };

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby="new-program-dialog-title"
    >
      <DialogTitle id="add-user-roles-dialog-title">
        Change Password
      </DialogTitle>
      <DialogContent>
        {notBlank(errors) && (
          <Box className={classes.errorContainer}>
            {errors.map((message, idx) => (
              <div key={idx}>{message}</div>
            ))}
          </Box>
        )}
        <Paper>
          <Box className={classes.form}>
            <Grid spacing={2} container>
              <Grid item md={12}>
                <TextField
                  label="New Password"
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  value={changePassword.password}
                  onChange={handleTextChange(["password"])}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          size="large"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Confirm Password"
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  value={changePassword.confirm}
                  onChange={handleTextChange(["confirm"])}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          size="large"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
